import { useEffect, useRef, useCallback } from 'react'
import { useAuthStore } from '../stores/authStore'

export type WSMessage = Record<string, unknown>

interface UseWebSocketOptions {
  onTelemetry?: (msg: WSMessage) => void
  onEvent?: (msg: WSMessage) => void
  deviceId?: string
  enabled?: boolean
}

export function useWebSocket({ onTelemetry, onEvent, deviceId, enabled = true }: UseWebSocketOptions) {
  const wsRef = useRef<WebSocket | null>(null)
  const backoffRef = useRef(1000)
  const reconnectTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
  const activeRef = useRef(false)

  const connect = useCallback(() => {
    const token = useAuthStore.getState().accessToken
    if (!token || !enabled) return

    const apiBase = import.meta.env.VITE_API_BASE || 'https://api.energysecurity.com'
    const wsBase = apiBase.replace(/^http/, 'ws')
    const url = `${wsBase}/ws?token=${token}`

    try {
      const ws = new WebSocket(url)
      wsRef.current = ws

      ws.onopen = () => {
        backoffRef.current = 1000
        activeRef.current = true
        ws.send(JSON.stringify({ type: 'subscribe', device_id: deviceId || '' }))
      }

      ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data) as WSMessage
          const type = msg.type as string | undefined
          if (type === 'telemetry' || type === 'plc_telemetry') {
            onTelemetry?.(msg)
          } else if (type === 'event') {
            onEvent?.(msg)
          }
        } catch {
          // ignore malformed messages
        }
      }

      ws.onclose = () => {
        activeRef.current = false
        wsRef.current = null
        reconnectTimer.current = setTimeout(() => {
          backoffRef.current = Math.min(backoffRef.current * 2, 30000)
          connect()
        }, backoffRef.current)
      }

      ws.onerror = () => {
        ws.close()
      }
    } catch {
      reconnectTimer.current = setTimeout(connect, backoffRef.current)
    }
  }, [onTelemetry, onEvent, deviceId, enabled])

  useEffect(() => {
    connect()
    return () => {
      clearTimeout(reconnectTimer.current)
      if (wsRef.current) {
        wsRef.current.close()
        wsRef.current = null
      }
    }
  }, [connect])

  return { isConnected: activeRef.current }
}
